import {
  logEvent,
  setUserId as fbSetUserId,
  setUserProperties as fbSetUserProperties,
} from 'firebase/analytics';
import { analytics } from 'config/firebase';   // synchronous — ready immediately
import { sanitizeParams } from './analyticsHelpers';

const IS_DEV = import.meta.env.DEV;

const BADGE = 'background:#E37400;color:#fff;padding:2px 8px;border-radius:4px;font-weight:bold;font-size:11px';
const LABEL = 'color:#E37400;font-weight:bold';
const DIM   = 'color:#888;font-size:11px';

class AnalyticsServiceClass {
  constructor() {
    this._enabled = true;
    this._analytics = analytics; // synchronous — no race condition

    if (IS_DEV) {
      if (this._analytics) {
        console.log(
          '%c GA4 %c Firebase Analytics ready ✓  debug_mode ON → events go to DebugView',
          BADGE, 'color:green;font-weight:bold'
        );
      } else {
        console.warn('%c GA4 %c Firebase Analytics failed to init — console-only', BADGE, 'color:orange;font-weight:bold');
      }
    }
  }

  configure({ enabled }) {
    this._enabled = !!enabled;
    if (IS_DEV) console.log('%c GA4 %c enabled =', BADGE, LABEL, this._enabled);
  }

  _log(eventName, params) {
    if (!IS_DEV) return;
    const dest = (this._analytics && this._enabled) ? '→ Firebase DebugView' : '(console only — Firebase not ready)';
    console.groupCollapsed(`%c GA4 %c ${eventName} %c ${dest}`, BADGE, LABEL, DIM);
    if (params && Object.keys(params).length) console.table(params);
    console.groupEnd();
  }

  _fire(eventName, rawParams = {}) {
    const params = sanitizeParams(rawParams);
    this._log(eventName, params);
    if (!this._enabled || !this._analytics) return;
    try {
      logEvent(this._analytics, eventName, params);
    } catch (e) {
      if (IS_DEV) console.error('%c GA4 %c logEvent failed:', BADGE, 'color:red', e.message);
    }
  }

  trackScreen(screenName, extraParams = {}) {
    this._fire('screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: screenName,
      ...extraParams,
    });
  }

  trackEvent(eventName, params = {}) {
    this._fire(eventName, params);
  }

  trackCTA(ctaName, screenName, params = {}) {
    this._fire('cta_click', { cta_name: ctaName, screen_name: screenName, ...params });
  }

  trackFormStart(formName, screenName) {
    this._fire('form_start', { form_name: formName, screen_name: screenName });
  }

  trackFormSubmit(formName, screenName, params = {}) {
    this._fire('form_submit', { form_name: formName, screen_name: screenName, ...params });
  }

  trackFormComplete(formName, screenName, params = {}) {
    this._fire('form_complete', { form_name: formName, screen_name: screenName, ...params });
  }

  trackFormSubmitError(formName, screenName, errorMessage) {
    this._fire('form_submit_error', {
      form_name: formName,
      screen_name: screenName,
      error_message: String(errorMessage).slice(0, 100),
    });
  }

  trackFormValidationError(formName, screenName, field) {
    this._fire('form_validation_error', { form_name: formName, screen_name: screenName, field });
  }

  trackJourneyStart(journeyName, params = {}) {
    this._fire('journey_start', { journey_name: journeyName, ...params });
  }

  trackJourneyStep(journeyName, step, params = {}) {
    this._fire('journey_step', { journey_name: journeyName, step, ...params });
  }

  trackJourneyComplete(journeyName, params = {}) {
    this._fire('journey_complete', { journey_name: journeyName, ...params });
  }

  trackJourneyFailure(journeyName, step, errorMessage) {
    this._fire('journey_failure', {
      journey_name: journeyName,
      step,
      error_message: String(errorMessage).slice(0, 100),
    });
  }

  trackApiFailure(url, status, errorType, feature) {
    this._fire('api_failure', {
      endpoint: String(url).slice(0, 100),
      status_code: status,
      error_type: errorType,
      feature,
    });
  }

  trackError(errorType, message, screenName) {
    this._fire('app_error', {
      error_type: errorType,
      error_message: String(message).slice(0, 100),
      screen_name: screenName,
    });
  }

  setUserId(id) {
    if (IS_DEV) console.log('%c GA4 %c setUserId →', BADGE, LABEL, id ? '<set>' : '<cleared>');
    if (!this._analytics) return;
    try { fbSetUserId(this._analytics, id ?? null); } catch (_) {}
  }

  setUserProperties(properties) {
    const safe = sanitizeParams(properties);
    if (IS_DEV) {
      console.groupCollapsed('%c GA4 %c setUserProperties', BADGE, LABEL);
      console.table(safe);
      console.groupEnd();
    }
    if (!this._analytics) return;
    try { fbSetUserProperties(this._analytics, safe); } catch (_) {}
  }
}

export const AnalyticsService = new AnalyticsServiceClass();
